import { useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { FieldPlacement } from '@/types/template-fields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { SignatureCanvas } from './SignatureCanvas';
import { useFormFieldResolver } from '@/hooks/use-form-documents';
import { usePdfPageRenderer, PDF_PLACEHOLDER_HEIGHT, PDF_MIN_SCALE, PDF_MAX_SCALE } from '@/hooks/use-pdf-page-renderer';

// Configure PDF.js worker to use local file (not CDN)
// This works with CSP policies and offline environments
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs?v=${pdfjs.version}-legacy`;
}

interface PublicFormViewerProps {
  pdfUrl: string;
  fieldPlacements: FieldPlacement[];
  fieldValues: Record<string, string>;
  claimData: Record<string, unknown>;
  onFieldChange: (fieldId: string, value: string) => void;
  onSignatureChange: (fieldId: string, signatureDataUrl: string) => void;
}

export function PublicFormViewer({
  pdfUrl,
  fieldPlacements,
  fieldValues,
  claimData,
  onFieldChange,
  onSignatureChange,
}: PublicFormViewerProps) {
  const [activeSignatureField, setActiveSignatureField] = useState<string | null>(null);

  // Use the field resolver hook
  const { resolveFieldValue } = useFormFieldResolver(claimData);

  const {
    isDesktop,
    numPages,
    scale,
    setScale,
    pageAspectRatio,
    currentPage,
    scrollContainerRef,
    getPageRef,
    nearViewport,
    measuredHeights,
    onDocumentLoadSuccess,
    mobileRenderWidth,
    getEffectiveScale,
    pageIndices,
  } = usePdfPageRenderer({ pdfUrl });

  // Group fields by page for efficient lookup
  const fieldsByPage = useMemo(() => {
    const map = new Map<number, FieldPlacement[]>();
    for (const f of fieldPlacements) {
      const arr = map.get(f.page) ?? [];
      arr.push(f);
      map.set(f.page, arr);
    }
    return map;
  }, [fieldPlacements]);

  const totalFields = fieldPlacements.filter(
    (f) => f.type === 'text_input' || f.type === 'signature',
  ).length;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <span className="text-sm text-muted-foreground">
          Page {currentPage} of {numPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.max(PDF_MIN_SCALE, +(s - 0.1).toFixed(1)))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.min(PDF_MAX_SCALE, +(s + 0.1).toFixed(1)))}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">
          {totalFields} field(s) to fill
        </div>
      </div>

      {/* Scrollable PDF Canvas — all pages stacked */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto flex flex-col items-center gap-6 p-8 bg-muted/50"
      >
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
          {pageIndices.map((pageIndex) => {
            const pageFields = fieldsByPage.get(pageIndex) ?? [];
            const isRendered = nearViewport.has(pageIndex);
            const effectiveScale = getEffectiveScale(pageIndex);
            return (
              <div
                key={pageIndex}
                data-page-index={pageIndex}
                ref={(el) => {
                  const ref = getPageRef(pageIndex);
                  (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  if (el && el.offsetHeight > 0) {
                    measuredHeights.current.set(pageIndex, el.offsetHeight);
                  }
                }}
                className="relative shadow-lg mb-6 overflow-hidden"
                style={
                  !isRendered
                    ? {
                        height:
                          measuredHeights.current.get(pageIndex) ??
                          (isDesktop
                            ? PDF_PLACEHOLDER_HEIGHT * scale
                            : Math.floor(mobileRenderWidth * pageAspectRatio)),
                        width: isDesktop ? '100%' : mobileRenderWidth,
                      }
                    : undefined
                }
              >
                {isRendered ? (
                  <>
                <Page
                  pageNumber={pageIndex + 1}
                  width={isDesktop ? undefined : mobileRenderWidth}
                  scale={isDesktop ? scale : undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />

                {/* Page label */}
                <div className="absolute top-0 right-0 bg-black/50 text-white text-xs px-2 py-0.5 rounded-bl pointer-events-none">
                  Page {pageIndex + 1}
                </div>

                {/* Field Overlays for this page */}
                {pageFields.map((field) => {
                  // Clamp position to [0, 100] then size to [0, remaining space]
                  const clampedX = Math.max(0, Math.min(field.x, 100));
                  const clampedY = Math.max(0, Math.min(field.y, 100));
                  const clampedW = Math.max(0, Math.min(field.width, 100 - clampedX));
                  const clampedH = Math.max(0, Math.min(field.height, 100 - clampedY));
                  const isInitialsField = field.fieldKey.startsWith('homeowner_initials_');
                  const isLongTextField = field.fieldKey.startsWith('homeowner_long_text_');
                  const isNumberField = field.fieldKey.startsWith('homeowner_number_');
                  const isDateField = field.fieldKey.startsWith('homeowner_date_');
                  const isCheckboxField = field.fieldKey.startsWith('homeowner_checkbox_');
                  const isMultiline = field.multiline ?? isLongTextField;
                  // Homeowner-fillable fields must render above (and stay clickable
                  // over) any read-only data/static overlays that overlap them, so a
                  // signature box is never covered or blocked by another field.
                  const isInteractive = field.type === 'text_input' || field.type === 'signature';
                  return (
                  <div
                    key={field.id}
                    className={`absolute overflow-hidden${isInteractive ? '' : ' pointer-events-none'}`}
                    style={{
                      left: `${clampedX}%`,
                      top: `${clampedY}%`,
                      width: `${clampedW}%`,
                      height: `${clampedH}%`,
                      zIndex: field.type === 'signature' ? 30 : isInteractive ? 20 : 10,
                    }}
                  >
                    {/* Data fields (read-only, pre-filled) */}
                    {field.type === 'data' && (
                      <div
                        className="w-full h-full flex items-center px-2"
                        style={{
                          fontSize: `${field.fontSize * effectiveScale}px`,
                          fontFamily: field.fontFamily,
                          textAlign: field.textAlign,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                        }}
                      >
                        <span className="text-foreground">
                          {resolveFieldValue(field)}
                        </span>
                      </div>
                    )}

                    {/* Static text fields (adjuster-authored, read-only) */}
                    {field.type === 'static_text' && field.staticContent && (
                      <div
                        className="w-full h-full flex items-center px-2"
                        style={{
                          fontSize: `${field.fontSize * effectiveScale}px`,
                          fontFamily: field.fontFamily,
                          textAlign: field.textAlign,
                          whiteSpace: 'pre-wrap',
                          overflow: 'hidden',
                        }}
                      >
                        <span className="text-foreground">{field.staticContent}</span>
                      </div>
                    )}

                    {/* Adjuster signature (read-only image) */}
                    {field.type === 'adjuster_signature' && field.staticContent && (
                      <div className="w-full h-full">
                        <img
                          src={field.staticContent}
                          alt="Adjuster Signature"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    {/* Text input fields (user fills) */}
                    {field.type === 'text_input' && (
                      <div className="w-full h-full">
                        {isCheckboxField ? (
                          <div className="w-full h-full flex items-center justify-center bg-white border border-blue-300 rounded-none">
                            <Checkbox
                              checked={fieldValues[field.id] === 'true'}
                              onCheckedChange={(checked) =>
                                onFieldChange(field.id, checked === true ? 'true' : '')
                              }
                              className="rounded-none"
                              style={{
                                height: `${Math.max(12, 16 * effectiveScale)}px`,
                                width: `${Math.max(12, 16 * effectiveScale)}px`,
                              }}
                            />
                          </div>
                        ) : isMultiline ? (
                          <Textarea
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => onFieldChange(field.id, e.target.value)}
                            placeholder={field.label}
                            required={field.required}
                            maxLength={5000}
                            // min-h-0 overrides the shadcn default min-h-[60px] so
                            // textareas placed in a short box don't get clipped by
                            // the field container's overflow-hidden.
                            className="w-full h-full min-h-0 resize-none px-1 py-0.5 text-sm bg-white border-blue-300 focus:border-blue-500 rounded-none"
                            style={{
                              fontSize: `${field.fontSize * effectiveScale}px`,
                              lineHeight: 1.2,
                              fontFamily: field.fontFamily,
                              textAlign: field.textAlign,
                            }}
                          />
                        ) : (
                          <Input
                            type={isDateField ? 'date' : isNumberField ? 'number' : 'text'}
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => {
                              const value = isInitialsField
                                ? e.target.value.replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase()
                                : e.target.value;
                              onFieldChange(field.id, value);
                            }}
                            placeholder={field.label}
                            required={field.required}
                            maxLength={isInitialsField ? 2 : undefined}
                            pattern={isInitialsField ? '[A-Za-z]{2}' : undefined}
                            // px-1 py-0 overrides the shadcn default px-3 py-2 so
                            // short/narrow fields don't clip their own text.
                            className="w-full h-full px-1 py-0 text-sm bg-white border-blue-300 focus:border-blue-500 rounded-none"
                            style={{
                              fontSize: `${field.fontSize * effectiveScale}px`,
                              lineHeight: 1.2,
                              fontFamily: field.fontFamily,
                              textAlign: field.textAlign,
                            }}
                          />
                        )}
                      </div>
                    )}

                    {field.type === 'signature' && (
                      <div className="w-full h-full">
                        {fieldValues[field.id] ? (
                          <div className="relative w-full h-full border-2 border-purple-300 rounded-none bg-white group">
                            <img
                              src={fieldValues[field.id]}
                              alt="Signature"
                              className="w-full h-full object-contain"
                            />
                            <button
                              className="absolute opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-purple-600 hover:text-purple-800 bg-white/90 rounded px-2"
                              style={{
                                top: '4px',
                                right: '4px',
                                height: `${Math.max(20, field.fontSize * 1.5) * effectiveScale}px`,
                                fontSize: `${Math.max(10, field.fontSize * 0.7) * effectiveScale}px`,
                              }}
                              onClick={() => {
                                onFieldChange(field.id, '');
                                setActiveSignatureField(field.id);
                              }}
                            >
                              Clear
                            </button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            // min-h-0 overrides the shadcn default min-h-9, and
                            // min-w-0 lets the flex child below actually shrink
                            // (and truncate) instead of overflowing the box.
                            className="w-full h-full min-h-0 min-w-0 overflow-hidden border-2 border-dashed border-purple-300 hover:border-purple-500 hover:bg-purple-50 px-1 py-0 text-xs rounded-none leading-none"
                            onClick={() => setActiveSignatureField(field.id)}
                            style={{
                              fontSize: `${Math.min(field.fontSize * 0.8, 12) * effectiveScale}px`,
                            }}
                          >
                            <span className="block w-full min-w-0 truncate text-purple-600">
                              Click to Sign: {field.label}
                            </span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full bg-muted/30 rounded text-muted-foreground text-sm">
                    Page {pageIndex + 1}
                  </div>
                )}
              </div>
            );
          })}
        </Document>
      </div>

      {/* Signature Dialog — bottom sheet on phones, centered card on larger screens */}
      {activeSignatureField && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="w-full max-w-2xl rounded-t-2xl bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg sm:rounded-lg sm:p-6">
            <h3 className="text-lg font-semibold mb-3 sm:mb-4">
              {
                fieldPlacements.find((f: FieldPlacement) => f.id === activeSignatureField)
                  ?.label || 'Sign Here'
              }
            </h3>
            <SignatureCanvas
              onSave={(dataUrl: string) => {
                onSignatureChange(activeSignatureField, dataUrl);
                setActiveSignatureField(null);
              }}
              onCancel={() => setActiveSignatureField(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
